import React, { useState, useMemo, useEffect } from "react";
import Grid from "@mui/material/Grid";
import UserKilometers from "../../../components/Charts/UserKilometers";
import UserMeetings from "../../../components/Charts/UserMeetings";

import FastAPIClient from "../../../client";
import config from "../../../config";
import UserCoverage from "../../../components/Charts/UserCoverage";
import MeetingTypeChart from "../../../components/Charts/MeetingTypeChart";

import FilterGrid from "../../../components/Charts/FilterGrid";
import Reports from "./reports";

import dayjs, { Dayjs } from "dayjs";

const client = new FastAPIClient(config);

interface User {
    user_id: string;
    first_name: string;
    last_name: string;
    team_name?: string | null;
    warehouse_name?: string | null;
    is_kilometers?: boolean | null;
}

type MonthlyStat = {
    user_id: string;
    first_name: string;
    last_name: string;
    warehouse_name: string;
    year: number;
    month: number;
    monthly_revenue: number;
    monthly_volume: number;
    monthly_margin: number;
    distinct_clients: number;
    team_name: string;
    product_level_1?: string;
    client_level_1?: string;
};

const SalesReps: React.FC = () => {
    const [selectedUser, setSelectedUser] = useState<string>("all");
    const [selectedWarehouse, setSelectedWarehouse] = useState<string>("all");
    const [data, setData] = useState<User[]>([]);
    const [, setTransactionData] = useState<MonthlyStat[]>([]);

    useEffect(() => {
        fetchActivity();
    }, []);

    const fetchActivity = async () => {
        try {
            const response = await client.fetchTeamMembers();
            console.log(response.results);
            setData(response.results);
        } catch (error) {
            console.error("Error fetching activity:", error);
        }
    };

    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
        dayjs().subtract(1, 'week').startOf('week'),
        dayjs().endOf('week'),
    ]);

    useEffect(() => {
        const [startDate, endDate] = dateRange;
        if (startDate && endDate) {
            const formattedStartDate = startDate.format("YYYY-MM-DD");
            const formattedEndDate = endDate.format("YYYY-MM-DD");
            fetchMonthlyStats(formattedStartDate, formattedEndDate);
        }
    }, [dateRange]);

    const fetchMonthlyStats = async (startDate: string, endDate: string) => {
        try {
            const response = await client.fetchMonthlyStats({
                startDate,
                endDate,
            });
            console.log("Monthly Stats", response.results);
            setTransactionData(response.results);
        } catch (error) {
            console.error("Error fetching monthly stats:", error);
        }
    };

    const filteredUsers =
        selectedWarehouse === "all"
            ? data
            : data.filter((user) => user.warehouse_name === selectedWarehouse);

    const uniqueUsers = useMemo(() => {
        return filteredUsers.map((user) => ({
            user_id: user.user_id,
            first_name: user.first_name,
            last_name: user.last_name,
            warehouse_name: user.warehouse_name,
        }));
    }, [filteredUsers]);

    const uniqueWarehouses = useMemo(() => {
        const warehouses = data
            .map((user) => user.warehouse_name)
            .filter(Boolean);
        return Array.from(new Set(warehouses));
    }, [data]);

    const handleUserSelectChange = (value: string) => {
        setSelectedUser(value);

        if (value !== "all") {
            const selectedUserData = data.find(
                (user) => `${user.first_name} ${user.last_name}` === value
            );
            if (selectedUserData) {
                setSelectedWarehouse(selectedUserData.warehouse_name || "all");
            }
        } else {
            setSelectedWarehouse("all");
        }
    };

    const handleWarehouseSelectChange = (value: string) => {
        setSelectedWarehouse(value);

        if (value !== "all") {
            data.find(
                (user) => user.warehouse_name === value
            );
        } else {
            setSelectedUser("all");
        }
    };

    useEffect(() => {
        const [startDate, endDate] = dateRange;
        if (startDate && endDate) {
            console.log("Start:", startDate.format("YYYY-MM-DD"));
            console.log("End:", endDate.format("YYYY-MM-DD"));
        }
    }, [dateRange]);

    const hasKilometerUsers = useMemo(() => {
        return data.some((user) => user.is_kilometers === true);
    }, [data]);

    return (
        <div className="w-full items-center flex flex-col justify-center">
            <div className="w-full">
                <FilterGrid
                    selectedUser={selectedUser}
                    selectedWarehouse={selectedWarehouse}
                    dateRange={dateRange}
                    uniqueUsers={uniqueUsers}
                    uniqueWarehouses={uniqueWarehouses}
                    onUserChange={handleUserSelectChange}
                    onWarehouseChange={handleWarehouseSelectChange}
                    onDateRangeChange={setDateRange}
                />
                <Grid container spacing={1} columns={{ xs: 1, md: 5 }}>
                    <Grid size={2}>
                        <div className="grid grid-cols-1 gap-4">
                            <div className="grid grid-cols-1">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div className="w-full border border-gray-200 rounded-md">
                                        <UserCoverage
                                            selectedUser={selectedUser}
                                            selectedWarehouse={selectedWarehouse}
                                            timePeriod={"timePeriod"}
                                            selectedTeam="all"
                                        />
                                    </div>
                                    <div className="w-full border border-gray-200 rounded-md">
                                        <MeetingTypeChart
                                            selectedUser={selectedUser}
                                            selectedTeam="all"
                                            selectedWarehouse={selectedWarehouse}
                                            dateRange={dateRange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="w-full border border-gray-200 rounded-md">
                                <UserMeetings
                                    selectedUser={selectedUser}
                                    selectedWarehouse={selectedWarehouse}
                                    selectedTeam="all"
                                    dateRange={dateRange}
                                />
                            </div>
                            {hasKilometerUsers && (
                                <div className="w-full border border-gray-200 rounded-md">
                                    <UserKilometers
                                        selectedUser={selectedUser}
                                        selectedTeam="all"
                                        selectedWarehouse={selectedWarehouse}
                                        dateRange={dateRange}
                                    />
                                </div>
                            )}
                        </div>
                    </Grid>

                    <Grid size={3}>
                        <Reports
                            selectedUser={selectedUser}
                            selectedWarehouse={selectedWarehouse}
                            dateRange={dateRange}
                            onUserChange={handleUserSelectChange}
                            onWarehouseChange={handleWarehouseSelectChange}
                        />
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};

export default SalesReps;
